import * as React from 'react';

import { parseCalendarDate } from '@rematter/common/dateTime';
import {
  Badge,
  Button,
  cn,
  DataGridColumn,
  LocalizedDate,
  Menu,
} from '@rematter/ui-web';
import { LucideEllipsis } from 'lucide-react';

import { AgentRecord } from '../../../_graphql/queryAgentList';
import { useTranslations } from '../../../i18n/useTranslations';
import { adminRoutes } from '../../../routes';
import { getToolDisplayName } from '../../../utils/toolRegistry';

type UseAgentsColumnsArgs = {
  onActivateToggle: (agent: AgentRecord) => Promise<void>;
  navigate: (
    route: string,
    opts: { params?: { id: string }; query?: { type: string } },
  ) => void;
};

export const useAgentsColumns = ({
  onActivateToggle,
  navigate,
}: UseAgentsColumnsArgs): DataGridColumn<AgentRecord>[] => {
  const t = useTranslations();

  return React.useMemo(
    () => [
      {
        key: 'name',
        header: t.aiManagement.agents.header.name,
        width: 250,
        minWidth: 200,
        cell: (cell: { row: AgentRecord }) => (
          <div className="font-medium text-neutral/90">{cell.row.name}</div>
        ),
      },
      {
        key: 'type',
        header: t.aiManagement.agents.header.type,
        width: 120,
        minWidth: 100,
        cell: (cell: { row: AgentRecord }) => {
          const typeLabel =
            {
              Agent: t.aiManagement.agents.type.agent,
              SubAgent: t.aiManagement.agents.type.subAgent,
              FileProcessor: t.aiManagement.agents.type.fileProcessor,
            }[cell.row.type] ?? cell.row.type;

          const typeColor =
            {
              Agent: 'primary' as const,
              SubAgent: 'teal' as const,
              FileProcessor: 'purple' as const,
            }[cell.row.type] ?? ('neutral' as const);

          return (
            <Badge color={typeColor} size="small">
              {typeLabel}
            </Badge>
          );
        },
      },
      {
        key: 'agentId',
        header: t.aiManagement.agents.header.agentId,
        width: 200,
        minWidth: 150,
        cell: (cell: { row: AgentRecord }) => (
          <div
            className="truncate font-mono text-sm text-neutral/70"
            title={cell.row.agentIdentifier ?? undefined}
          >
            {cell.row.agentIdentifier ?? '-'}
          </div>
        ),
      },
      {
        key: 'status',
        header: t.aiManagement.agents.header.status,
        width: 120,
        minWidth: 100,
        cell: (cell: { row: AgentRecord }) => (
          <Badge color={cell.row.isActive ? 'success' : 'neutral'} size="small">
            {cell.row.isActive
              ? t.aiManagement.agents.status.active
              : t.aiManagement.agents.status.inactive}
          </Badge>
        ),
      },
      {
        key: 'provider',
        header: t.aiManagement.agents.header.provider,
        width: 120,
        minWidth: 50,
        cell: (cell: { row: AgentRecord }) => (
          <div className="font-medium text-neutral/90">
            {cell.row.modelProvider}
          </div>
        ),
      },
      {
        key: 'model',
        header: t.aiManagement.agents.header.model,
        width: 120,
        minWidth: 100,
        cell: (cell: { row: AgentRecord }) => (
          <div className="font-medium text-neutral/90">{cell.row.model}</div>
        ),
      },
      {
        key: 'tools',
        header: t.aiManagement.agents.header.tools,
        width: 150,
        minWidth: 100,
        cell: (cell: { row: AgentRecord }) => {
          const toolCount = cell.row.tools?.length ?? 0;
          if (toolCount === 0) {
            return <span className="text-neutral/50">-</span>;
          }

          const toolNames = cell.row.tools
            .map((toolId) => getToolDisplayName(toolId, t) ?? toolId)
            .join(', ');

          return (
            <div className="group relative">
              <Badge color="neutral" size="small">
                {toolCount}{' '}
                {toolCount === 1
                  ? t.aiManagement.agents.toolCount.singular
                  : t.aiManagement.agents.toolCount.plural}
              </Badge>
              <div
                className={cn([
                  'pointer-events-none invisible absolute bottom-full left-0 z-50 mb-2 w-64 rounded border border-neutral/10 p-2 text-xs',
                  'text-neutral/90 opacity-0 shadow-lg transition-opacity group-hover:visible group-hover:opacity-100',
                ])}
              >
                {toolNames}
              </div>
            </div>
          );
        },
      },
      {
        key: 'version',
        header: t.aiManagement.agents.header.version,
        width: 100,
        minWidth: 80,
        cell: (cell: { row: AgentRecord }) => (
          <div className="text-neutral/60">v{cell.row.version}</div>
        ),
      },
      {
        key: 'createdAt',
        header: t.aiManagement.agents.header.createdAt,
        width: 150,
        minWidth: 120,
        cell: (cell: { row: AgentRecord }) => {
          const date = parseCalendarDate(cell.row.timestamps.createdAt);
          return <LocalizedDate date={date} dateStyle="medium" />;
        },
      },
      {
        key: 'actions',
        header: t.aiManagement.agents.header.actions,
        width: 50,
        minWidth: 50,
        align: 'center' as const,
        cell: (cell: { row: AgentRecord }) => (
          <Menu
            trigger={
              <Button size="small" variant="ghost">
                <LucideEllipsis className="size-4" />
              </Button>
            }
            items={[
              {
                label: t.aiManagement.agents.actions.edit,
                onAction: () =>
                  navigate(adminRoutes.aiAgentDetail, {
                    params: { id: cell.row.id },
                  }),
              },
              {
                label: cell.row.isActive
                  ? t.aiManagement.agents.actions.deactivate
                  : t.aiManagement.agents.actions.activate,
                onAction: () => onActivateToggle(cell.row),
              },
            ]}
          />
        ),
      },
    ],
    [t, navigate, onActivateToggle],
  );
};
